import { recoverReply, ReplyFailure } from './recovery';
import { safeCitationUrl, type Citation } from '@24talk/shared/citations';
import { messageMarkdown, messageMarkdownStyles, type MessageNode } from '@24talk/shared/markdown';
import {
  browserLocale,
  dictionaries,
  isLocale,
  saveLocale,
  type Locale,
  type TranslationKey,
} from './i18n';

interface Config {
  publicId: string;
  displayName: string;
  greeting: string;
  primaryColor: string;
  defaultLocale?: string;
  greetingId?: string;
  greetingEn?: string;
  contactCaptureEnabled?: boolean;
  humanHandoffEnabled?: boolean;
}
interface Message {
  role: 'visitor' | 'assistant';
  text: string;
  citations?: Citation[];
  pending?: boolean;
}
const styles = `
:host{all:initial;font-family:Inter,ui-sans-serif,system-ui,sans-serif;color:#182033;--brand:#3156d3}.wrap{position:fixed;z-index:2147483000;bottom:20px;right:20px}.wrap.left{right:auto;left:20px}.toggle{width:58px;height:58px;border:0;border-radius:50%;background:var(--brand);color:#fff;box-shadow:0 10px 30px #16213b40;font-size:24px;cursor:pointer}.panel{position:absolute;bottom:72px;right:0;width:min(380px,calc(100vw - 24px));height:min(590px,calc(100vh - 110px));background:#fff;border:1px solid #dce2ec;border-radius:18px;box-shadow:0 20px 55px #16213b30;display:grid;grid-template-rows:auto 1fr auto;overflow:hidden}.left .panel{right:auto;left:0}.hidden{display:none}.head{background:var(--brand);color:#fff;padding:16px 18px;display:flex;justify-content:space-between;align-items:center}.head strong{font-size:15px}.close{border:0;background:transparent;color:#fff;font-size:24px;cursor:pointer}.messages{padding:15px;overflow:auto;background:#f7f8fb;display:flex;flex-direction:column;gap:10px}.bubble{max-width:84%;padding:10px 12px;border-radius:14px;background:#fff;border:1px solid #e3e7ee;line-height:1.45;font-size:14px;white-space:pre-wrap}.bubble.pending{min-width:42px}.typing-indicator{display:inline-flex;align-items:center;gap:4px;height:20px}.typing-dot{width:7px;height:7px;border-radius:50%;background:#77839a;animation:talkTypingPulse 1.2s ease-in-out infinite}.typing-dot:nth-child(2){animation-delay:.16s}.typing-dot:nth-child(3){animation-delay:.32s}@keyframes talkTypingPulse{0%,60%,100%{opacity:.35;transform:translateY(0)}30%{opacity:1;transform:translateY(-3px)}}.visitor{align-self:flex-end;background:var(--brand);border-color:var(--brand);color:#fff}.bubble small{display:block;margin-top:7px;color:#68758b;font-size:11px}.visitor small{color:#ffffffc7}.composer{border-top:1px solid #e4e8ef;padding:10px;background:#fff}.row{display:flex;gap:7px}.composer input{min-width:0;flex:1;border:1px solid #cbd3df;border-radius:10px;padding:10px;font:inherit}.composer button,.actions button,.lead button{border:1px solid #cbd3df;border-radius:9px;background:#fff;padding:8px 10px;font:inherit;font-weight:700;cursor:pointer}.composer .send{background:var(--brand);border-color:var(--brand);color:#fff}.actions{display:flex;gap:6px;margin-top:8px}.actions button{font-size:11px}.status{font-size:12px;color:#66728a;padding:3px 0}.error{color:#b42335}.lead{display:grid;gap:7px;margin-top:8px;padding:10px;background:#f2f4f8;border-radius:10px}.lead-header{display:flex;align-items:center;justify-content:space-between;gap:10px}.lead-header strong{font-size:13px}.lead .lead-close{display:grid;place-items:center;width:28px;height:28px;padding:0;border:0;background:transparent;font-size:20px;line-height:1}.lead label{font-size:11px;font-weight:700}.lead input{width:100%;box-sizing:border-box;margin-top:3px}.lead-actions{display:flex;gap:7px}.lead-actions button{flex:1}.consent{display:flex;gap:6px;align-items:start}.consent input{width:auto}.retry{margin-left:6px}.dark .panel,.dark .composer{background:#151b29;color:#eef2fa;border-color:#354057}.dark .messages{background:#0e1420}.dark .bubble{background:#1d2637;border-color:#354057;color:#eef2fa}.dark .typing-dot{background:#b8c2d6}.dark .composer input,.dark .composer button,.dark .lead{background:#202a3c;border-color:#43506a;color:#eef2fa}.dark .status{color:#b8c2d6}.toggle:focus-visible,.close:focus-visible,button:focus-visible,input:focus-visible{outline:3px solid #aebcff;outline-offset:2px}@media(max-width:520px){.wrap{bottom:12px;right:12px}.wrap.left{left:12px}.panel{position:fixed;inset:12px 12px 82px;width:auto;height:auto}.left .panel{inset:12px 12px 82px}}@media(prefers-reduced-motion:reduce){*{scroll-behavior:auto!important}.typing-dot{animation:none;opacity:.65}}
`;

const element = <K extends keyof HTMLElementTagNameMap>(
  name: K,
  className?: string,
  text?: string,
) => {
  const node = document.createElement(name);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
};
const formText = (data: FormData, name: string): string => {
  const value = data.get(name);
  return typeof value === 'string' ? value : '';
};

function renderMessageNode(node: MessageNode): Node {
  if (typeof node === 'string') return document.createTextNode(node);
  const result = element(node.tag);
  if (node.tag === 'a' && node.href) {
    result.setAttribute('href', node.href);
    result.setAttribute('target', '_blank');
    result.setAttribute('rel', 'noopener noreferrer');
    result.classList.add('citation');
  }
  if (node.tag === 'ol' && node.start !== undefined)
    result.setAttribute('start', String(node.start));
  result.append(...node.children.map(renderMessageNode));
  return result;
}

export async function mountWidget(input: {
  agentId: string;
  apiBaseUrl: string;
  position?: string;
  theme?: string;
  container?: HTMLElement;
  signal?: AbortSignal;
  onLocaleChange?: (locale: Locale) => void;
}): Promise<void> {
  if (input.signal?.aborted) return;
  let locale = browserLocale(input.agentId);
  const t = (key: TranslationKey) => dictionaries[locale][key];
  input.onLocaleChange?.(locale);
  const target = input.container ?? document.body;
  if (target.querySelector(`[data-24talk-agent="${CSS.escape(input.agentId)}"]`)) return;
  const host = element('div');
  host.lang = locale;
  host.dataset['24talkAgent'] = input.agentId;
  const shadow = host.attachShadow({ mode: 'open' });
  const style = element('style');
  style.textContent =
    styles +
    messageMarkdownStyles +
    `
    :host{display:block}.inline{position:relative;inset:auto;width:100%;z-index:auto}
    .inline .panel{position:relative;inset:auto;box-sizing:border-box;width:100%;height:600px;max-height:80vh;min-height:320px;box-shadow:none}
    .unavailable{padding:20px;border:1px solid #dce2ec;border-radius:12px;background:#fff;color:#182033;font:14px/1.5 system-ui}
    .wrap> .lead{position:absolute;bottom:72px;right:0;z-index:1;box-sizing:border-box;width:min(320px,calc(100vw - 24px));max-height:calc(100dvh - 110px);overflow-y:auto;overscroll-behavior:contain}
    .inline> .lead{bottom:10px;right:10px;width:min(320px,calc(100% - 20px));max-height:min(calc(100% - 20px),calc(100dvh - 24px))}
    .left:not(.inline)> .lead{right:auto;left:0}
  `;
  style.textContent +=
    '.citation{color:#2448b8;text-decoration:underline;overflow-wrap:anywhere}.bubble small .citation{display:inline-flex;align-items:center;min-height:44px}.citation:focus-visible{outline:3px solid #3156d3;outline-offset:2px}.dark .bubble small{color:#b8c2d6}.dark .citation{color:#b9cbff}.dark .citation:focus-visible{outline-color:#b9cbff}';
  style.textContent +=
    '.language{max-width:130px;margin-left:auto;margin-right:8px;padding:5px;border-radius:6px;font:inherit}.language:focus-visible{outline:3px solid #aebcff}';
  style.textContent +=
    '.status{display:flex;flex-wrap:wrap;align-items:center;gap:6px}.status>span:first-child{flex-basis:100%}.status .retry{min-height:44px;margin-left:0;border-color:#66728a;color:#182033}.status .retry:focus-visible{outline-color:#3156d3}.dark .status .retry{border-color:#b8c2d6;color:#eef2fa}.dark .status .retry:focus-visible{outline-color:#b9cbff}';
  const translated = <T extends HTMLElement>(
    node: T,
    key: TranslationKey,
    attribute?: 'aria-label' | 'placeholder',
  ): T => {
    node.dataset[
      attribute === 'aria-label'
        ? 'labelKey'
        : attribute === 'placeholder'
          ? 'placeholderKey'
          : 'textKey'
    ] = key;
    if (attribute) node.setAttribute(attribute, t(key));
    else node.textContent = t(key);
    return node;
  };
  const refreshText = () => {
    shadow
      .querySelectorAll<HTMLElement>('[data-text-key], [data-label-key], [data-placeholder-key]')
      .forEach((node) => {
        for (const [data, attribute] of [
          ['textKey', ''],
          ['labelKey', 'aria-label'],
          ['placeholderKey', 'placeholder'],
        ] as const) {
          const key = node.dataset[data] as TranslationKey | undefined;
          if (key) {
            if (attribute) node.setAttribute(attribute, t(key));
            else node.textContent = t(key);
          }
        }
      });
    host.lang = locale;
  };
  shadow.append(style);
  target.append(host);
  const controller = new AbortController();
  input.signal?.addEventListener(
    'abort',
    () => {
      controller.abort();
      host.remove();
    },
    { once: true },
  );
  const loading = translated(element('p', 'unavailable'), 'loading');
  loading.setAttribute('role', 'status');
  shadow.append(loading);
  let config: Config;
  try {
    const configResponse = await fetch(
      `${input.apiBaseUrl}/api/v1/public/agents/${encodeURIComponent(input.agentId)}`,
      { credentials: 'omit', signal: controller.signal },
    );
    if (!configResponse.ok) throw new Error('Unavailable');
    config = (await configResponse.json()) as Config;
  } catch {
    if (controller.signal.aborted) return;
    translated(loading, 'unavailable');
    loading.setAttribute('role', 'alert');
    return;
  }
  if (controller.signal.aborted) return;
  locale = browserLocale(input.agentId, config.defaultLocale);
  input.onLocaleChange?.(locale);
  host.lang = locale;
  loading.remove();
  const greeting = () =>
    (locale === 'id' ? config.greetingId : config.greetingEn)?.trim() || config.greeting;
  const messages: Message[] = [{ role: 'assistant', text: greeting() }];
  let sessionId: string | undefined;
  let busy = false;
  const wrap = element(
    'div',
    `wrap ${input.container ? 'inline' : ''} ${input.position === 'left' ? 'left' : ''} ${input.theme === 'dark' ? 'dark' : ''}`,
  );
  wrap.style.setProperty('--brand', config.primaryColor);
  const panel = element('section', input.container ? 'panel' : 'panel hidden');
  panel.setAttribute('role', input.container ? 'region' : 'dialog');
  panel.setAttribute('aria-label', `${config.displayName} ${t('chat')}`);
  const head = element('header', 'head');
  head.append(element('strong', '', config.displayName));
  const language = translated(element('select', 'language'), 'language', 'aria-label');
  for (const [value, label] of [
    ['id', 'Bahasa Indonesia'],
    ['en', 'English'],
  ]) {
    const option = element('option', '', label);
    option.value = value!;
    language.append(option);
  }
  language.value = locale;
  head.append(language);
  const close = element('button', 'close', '×');
  close.type = 'button';
  translated(close, 'close', 'aria-label');
  if (!input.container) head.append(close);
  const list = element('div', 'messages');
  list.setAttribute('role', 'log');
  list.setAttribute('aria-live', 'polite');
  const composer = element('form', 'composer');
  const row = element('div', 'row');
  const field = element('input');
  translated(field, 'placeholder', 'placeholder');
  field.maxLength = 4000;
  translated(field, 'message', 'aria-label');
  const send = translated(element('button', 'send'), 'send');
  send.type = 'submit';
  row.append(field, send);
  const status = element('div', 'status');
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.style.overflowWrap = 'anywhere';
  status.setAttribute('role', 'status');
  const actions = element('div', 'actions');
  const leadButton = translated(element('button'), 'contact');
  leadButton.type = 'button';
  const handoffButton = translated(element('button'), 'handoff');
  handoffButton.type = 'button';
  if (config.contactCaptureEnabled !== false) actions.append(leadButton);
  if (config.humanHandoffEnabled !== false) actions.append(handoffButton);
  composer.append(row, status);
  if (actions.childElementCount) composer.append(actions);
  const toggle = element('button', 'toggle', '💬');
  toggle.type = 'button';
  translated(toggle, 'open', 'aria-label');
  toggle.setAttribute('aria-expanded', 'false');
  wrap.append(panel);
  if (!input.container) wrap.append(toggle);
  panel.append(head, list, composer);
  shadow.append(wrap);
  const render = () => {
    list.replaceChildren(
      ...messages.map((message) => {
        const bubble = element('article', `bubble ${message.role}`, message.text);
        if (message.role === 'assistant') {
          const content = element('div', 'message-markdown');
          content.append(
            ...messageMarkdown(message.text, message.citations).map(renderMessageNode),
          );
          bubble.replaceChildren(content);
        }
        if (message.pending) {
          bubble.classList.add('pending');
          bubble.setAttribute('role', 'status');
          bubble.setAttribute('aria-label', `${config.displayName} ${t('preparing')}`);
          const indicator = element('span', 'typing-indicator');
          indicator.setAttribute('aria-hidden', 'true');
          indicator.append(
            element('span', 'typing-dot'),
            element('span', 'typing-dot'),
            element('span', 'typing-dot'),
          );
          bubble.append(indicator);
        }
        if (message.citations?.length) {
          const sources = element('small', '', `${t('sources')}: `);
          for (const [index, citation] of message.citations.entries()) {
            if (index) sources.append(document.createTextNode(', '));
            const url = safeCitationUrl(citation.url);
            if (url) {
              const link = element('a', 'citation', citation.title || new URL(url).hostname);
              link.href = url;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              sources.append(link);
            } else sources.append(document.createTextNode(citation.filename ?? t('knowledge')));
          }
          bubble.append(sources);
        }
        return bubble;
      }),
    );
    list.scrollTop = list.scrollHeight;
  };
  language.onchange = () => {
    if (!isLocale(language.value)) return;
    locale = language.value;
    saveLocale(input.agentId, locale);
    messages[0]!.text = greeting();
    panel.setAttribute('aria-label', `${config.displayName} ${t('chat')}`);
    refreshText();
    render();
    input.onLocaleChange?.(locale);
  };
  const setStatus = (key?: TranslationKey) => {
    delete status.dataset.textKey;
    status.replaceChildren();
    if (key) status.append(translated(element('span'), key));
  };
  const setOpen = (open: boolean) => {
    panel.classList.toggle('hidden', !open);
    toggle.setAttribute('aria-expanded', String(open));
    toggle.textContent = open ? '×' : '💬';
    if (!open) wrap.querySelector('.lead')?.remove();
    if (open) field.focus();
  };
  toggle.onclick = () => setOpen(panel.classList.contains('hidden'));
  close.onclick = () => setOpen(false);
  render();
  const sendMessage = async (text: string, previousBody?: string) => {
    if (busy) return;
    busy = true;
    send.disabled = true;
    setStatus();
    status.classList.remove('error');
    const visitorMessage: Message = { role: 'visitor', text };
    const pendingReply: Message = { role: 'assistant', text: '', pending: true };
    messages.push(visitorMessage, pendingReply);
    render();
    const body =
      previousBody ??
      JSON.stringify({
        requestId: crypto.randomUUID(),
        conversationId: sessionId,
        message: text,
        locale,
      });
    try {
      const reply = await recoverReply({
        url: `${input.apiBaseUrl}/api/v1/public/chat/agents/${encodeURIComponent(input.agentId)}/messages`,
        body,
        signal: controller.signal,
        onSession: (id) => {
          sessionId = id;
        },
        onRecovering: () => setStatus('recovering'),
      });
      pendingReply.text = reply.text;
      pendingReply.citations = reply.citations;
      pendingReply.pending = false;
      if (!pendingReply.text.trim()) throw new Error('Assistant returned an empty response');
      render();
      setStatus();
    } catch (error) {
      if (controller.signal.aborted) return;
      const pendingIndex = messages.indexOf(pendingReply);
      if (pendingIndex >= 0) messages.splice(pendingIndex, 1);
      render();
      const code = error instanceof ReplyFailure ? error.code : 'CHAT_FAILED';
      const key: TranslationKey =
        code === 'WEB_SEARCH_QUOTA'
          ? 'quotaReached'
          : code === 'AI_TIMEOUT'
            ? 'replyTimeout'
            : code === 'AI_MISSING_SOURCES' || code === 'AI_SOURCE_REJECTED'
              ? 'replySources'
              : code === 'DELIVERY_UNCONFIRMED'
                ? 'unconfirmed'
                : code === 'CHAT_RATE_LIMIT'
                  ? 'rateLimited'
                  : 'failed';
      setStatus(key);
      status.classList.add('error');
      if (
        error instanceof ReplyFailure &&
        error.diagnosticId &&
        /^[a-z0-9]{1,40}$/i.test(error.diagnosticId)
      ) {
        const reference = element('span');
        reference.textContent = ` (${error.diagnosticId})`;
        status.append(reference);
      }
      if (code !== 'DELIVERY_UNCONFIRMED') return;
      const retry = translated(element('button', 'retry'), 'checkReply');
      retry.type = 'button';
      retry.onclick = () => {
        const visitorIndex = messages.indexOf(visitorMessage);
        if (visitorIndex >= 0) messages.splice(visitorIndex, 1);
        void sendMessage(text, body);
      };
      status.append(retry);
      status.classList.add('error');
    } finally {
      busy = false;
      send.disabled = false;
    }
  };
  composer.onsubmit = (event) => {
    event.preventDefault();
    const text = field.value.trim();
    if (!text) return;
    field.value = '';
    void sendMessage(text);
  };
  leadButton.onclick = () => {
    const existingForm = wrap.querySelector<HTMLFormElement>('.lead');
    if (existingForm) {
      existingForm.querySelector<HTMLInputElement>('input')?.focus();
      return;
    }
    const form = element('form', 'lead');
    form.setAttribute('role', 'dialog');
    translated(form, 'details', 'aria-label');
    form.innerHTML =
      '<div class="lead-header"><strong data-text-key="contact"></strong><button type="button" class="lead-close" data-label-key="closeDetails">×</button></div><label><span data-text-key="name"></span><input name="name" maxlength="120"></label><label><span data-text-key="email"></span><input name="email" type="email" maxlength="320"></label><label><span data-text-key="phone"></span><input name="phone" maxlength="40"></label><label class="consent"><input name="consent" type="checkbox" required><span data-text-key="consent"></span></label><div class="lead-actions"><button type="button" class="lead-cancel" data-text-key="cancel"></button><button type="submit" data-text-key="submit"></button></div>';
    // Keep the contact form outside the message form: nested forms bubble submits.
    wrap.append(form);
    refreshText();
    const dismissForm = () => {
      form.remove();
      leadButton.focus();
    };
    form.querySelector<HTMLButtonElement>('.lead-close')!.onclick = dismissForm;
    form.querySelector<HTMLButtonElement>('.lead-cancel')!.onclick = dismissForm;
    form.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      dismissForm();
    });
    form.querySelector<HTMLInputElement>('input')?.focus();
    form.onsubmit = async (event) => {
      event.preventDefault();
      if (!sessionId) {
        setStatus('contactFirst');
        return;
      }
      const data = new FormData(form);
      const payload = {
        name: formText(data, 'name') || undefined,
        email: formText(data, 'email') || undefined,
        phone: formText(data, 'phone') || undefined,
        consent: data.get('consent') === 'on',
        idempotencyKey: crypto.randomUUID(),
      };
      const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      submitButton.disabled = true;
      try {
        const response = await fetch(
          `${input.apiBaseUrl}/api/v1/public/chat/${sessionId}/actions/capture-lead`,
          {
            method: 'POST',
            credentials: 'omit',
            signal: controller.signal,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          },
        );
        setStatus(response.ok ? 'saved' : 'invalidContact');
        if (response.ok) dismissForm();
      } catch {
        setStatus('contactFailed');
      } finally {
        submitButton.disabled = false;
      }
    };
  };
  handoffButton.onclick = async () => {
    if (!sessionId) {
      setStatus('handoffFirst');
      return;
    }
    handoffButton.disabled = true;
    try {
      const response = await fetch(
        `${input.apiBaseUrl}/api/v1/public/chat/${sessionId}/actions/request-handoff`,
        {
          method: 'POST',
          credentials: 'omit',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reason: 'Visitor requested human support from the widget.',
            idempotencyKey: crypto.randomUUID(),
          }),
        },
      );
      setStatus(response.ok ? 'handoffSaved' : 'handoffFailed');
    } catch {
      setStatus('handoffFailed');
    } finally {
      handoffButton.disabled = false;
    }
  };
}

// The classic loader passes public configuration to this external module in its URL.
const moduleUrl = new URL(import.meta.url);
const autoAgentId = moduleUrl.searchParams.get('agentId');
if (autoAgentId) {
  const targetId = moduleUrl.searchParams.get('targetId');
  const container = targetId ? document.getElementById(targetId) : undefined;
  if (!targetId || container) {
    void mountWidget({
      agentId: autoAgentId,
      apiBaseUrl: moduleUrl.searchParams.get('apiBaseUrl') ?? moduleUrl.origin,
      ...(moduleUrl.searchParams.get('position')
        ? { position: moduleUrl.searchParams.get('position')! }
        : {}),
      ...(moduleUrl.searchParams.get('theme')
        ? { theme: moduleUrl.searchParams.get('theme')! }
        : {}),
      ...(container ? { container } : {}),
    });
  }
}
